import type { JSX } from "react";
// Renders an amount in the user's selected currency, with the original
// currency shown small/grey beneath when it differs.
//
// `native` is the currency the value is stored in. Pass the row's
// `currency` field; for already-EUR-aggregated analytics, pass "EUR".

import { cn } from "../lib/utils.js";
import { useCurrency } from "../contexts/currency/index.js";
import { useNumberFormat } from "../contexts/numberFormat/index.js";

interface MoneyProps {
  amountMinor: number | string | bigint;
  native: string;
  /** Render inline (default) or as block with subtitle below. */
  block?: boolean;
  /** Optional sign prefix, e.g. "-" or "+", placed before both lines. */
  sign?: string;
}

export function Money({ amountMinor, native, block, sign = "" }: MoneyProps): JSX.Element {
  const { selected, convert, ratesReady } = useCurrency();
  const { formatMoney } = useNumberFormat();
  const amt = Number(amountMinor);
  const nativeText = `${sign}${formatMoney(amt, native)}`;

  if (native === selected) {
    return <span>{nativeText}</span>;
  }

  const converted = convert(amt, native);
  if (converted === null) {
    // Rates not loaded yet → show native only with hint.
    return (
      <span className={block ? "block" : "inline-block"}>
        <span>{nativeText}</span>
        {!ratesReady && (
          <span className="text-muted-foreground block text-[10px] opacity-70">converting…</span>
        )}
      </span>
    );
  }

  return (
    <span className={cn("leading-tight", block ? "block" : "inline-block")}>
      <span>{`${sign}${formatMoney(converted, selected)}`}</span>
      <span className="text-muted-foreground block text-[0.65em] font-normal opacity-70">
        {nativeText}
      </span>
    </span>
  );
}
